/**
 * supabase.js — Supabase client + authenticated fetch.
 *
 * PRODUCTION FIX: The cached Supabase access token expires after 1 hour.
 * On page reload, getSession() returns the expired token from localStorage.
 * This file handles it at two levels:
 *
 *   1. AIMail's auth useEffect calls refreshSession() BEFORE setting user
 *      state, ensuring all downstream API calls get a fresh token.
 *
 *   2. apiFetch auto-retries on 401 as a safety net — if a token expires
 *      mid-session, the retry refreshes and retries transparently.
 */
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// ── Extension OAuth (chrome.identity) ────────────────────────────────────────
//
// The Chrome side panel can't perform a top-level OAuth redirect, so we drive
// the flow through chrome.identity.launchWebAuthFlow using the extension's own
// https://<extension-id>.chromiumapp.org/ redirect URL. This replaces the older
// approach of opening a tab and watching chrome.tabs.onUpdated — which required
// the broad "tabs" permission. Requires the "identity" permission instead.
//
// IMPORTANT (one-time setup): the redirect URL returned by
// chrome.identity.getRedirectURL() must be added to Supabase Auth →
// "URL Configuration" → Redirect URLs. The extension ID (and therefore the URL)
// is stable once the extension is published to the Chrome Web Store; for local
// unpacked testing, add that build's chromiumapp.org URL too.
//
// Resolves to { access_token, refresh_token, provider_token } on success.
// provider_token is the Google access token (present when an OAuth scope such as
// gmail.readonly is requested) used for direct Gmail API calls.
export async function extensionOAuth(provider, { scopes, queryParams } = {}) {
  if (!supabase) throw new Error('Auth not configured');
  if (typeof chrome === 'undefined' || !chrome.identity?.launchWebAuthFlow) {
    throw new Error('chrome.identity is unavailable');
  }

  const redirectTo = chrome.identity.getRedirectURL();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo, skipBrowserRedirect: true, scopes, queryParams },
  });
  if (error) throw error;
  if (!data?.url) throw new Error('Failed to build OAuth URL');

  const redirectUrl = await chrome.identity.launchWebAuthFlow({
    url: data.url,
    interactive: true,
  });
  if (!redirectUrl) throw new Error('Sign-in was cancelled');

  // Supabase returns the session in the URL fragment (#access_token=…); some
  // error cases come back on the query string instead.
  const frag = redirectUrl.split('#')[1] || redirectUrl.split('?')[1] || '';
  const params = new URLSearchParams(frag);
  const errDesc = params.get('error_description') || params.get('error');
  if (errDesc) throw new Error(decodeURIComponent(errDesc));

  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token') || '';
  const provider_token = params.get('provider_token');
  if (!access_token) throw new Error('No access token in sign-in response');

  // Establish the Supabase session — fires onAuthStateChange (SIGNED_IN).
  const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
  if (setErr) throw setErr;

  return { access_token, refresh_token, provider_token };
}

// Provider of the most recent sign-in attempt, so the SIGNED_IN handler can
// label the `auth_completed` funnel event with the method used.
let _lastAuthMethod = null;
export const lastAuthMethod = () => _lastAuthMethod;

/**
 * startOAuth — single source of truth for starting an OAuth sign-in, shared by
 * AuthModal and the inline SignupNudge so "Continue with Google" is true
 * one-click everywhere. Uses chrome.identity in the extension side panel (which
 * can't do a top-level redirect) and the standard redirect on web. Throws on
 * error; callers own the loading/error UI.
 */
export async function startOAuth(provider) {
  if (!supabase) throw new Error('Auth not configured');
  _lastAuthMethod = provider;
  // Supabase requires Azure to return a valid email; Google needs no scopes.
  const scopes = provider === 'azure' ? 'email' : undefined;
  const isExtension = typeof window !== 'undefined' &&
    (window.__AIMAIL_IS_EXTENSION || window.__AIMAIL_FORCE_COMPACT);
  if (isExtension && typeof chrome !== 'undefined' && chrome.identity) {
    return extensionOAuth(provider, scopes ? { scopes } : {});
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: window.location.origin + window.location.pathname, scopes },
  });
  if (error) throw error;
}

// ── Token management ─────────────────────────────────────────────────────────

export async function getAccessToken() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  const session = data?.session;
  if (!session) return null;
  // Proactively refresh if expired or within 60 s of expiry
  if (session.expires_at && Date.now() / 1000 > session.expires_at - 60) {
    return refreshAccessToken();
  }
  return session.access_token;
}

// Refresh lock: if multiple API calls get 401 simultaneously,
// only one refresh happens — the others wait for the same promise.
let _refreshPromise = null;

export async function refreshAccessToken() {
  if (!supabase) return null;
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('[Auth] Token refresh failed:', error.message);
        return null;
      }
      console.log('[Auth] Token refreshed successfully');
      return data?.session?.access_token || null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// ── Authenticated fetch with auto-retry ──────────────────────────────────────

export async function apiFetch(path, options = {}) {
  const apiBase = import.meta.env.VITE_API_BASE || "";
  const url = apiBase ? `${apiBase}${path}` : path;

  // First attempt with current token
  const token = await getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (e) {
    // An abort is the CALLER cancelling (Stop, unmount) — not a network
    // problem: no scary log, and never retry with an already-dead signal.
    if (e?.name === 'AbortError' || options.signal?.aborted || /abort/i.test(e?.message || '')) throw e;
    // Network error (CORS block, offline, etc.) — try refresh + retry
    console.warn('[apiFetch] Network error on', path, ':', e.message);
    if (token) {
      const freshToken = await refreshAccessToken();
      if (freshToken) {
        const retryHeaders = { ...headers, 'Authorization': `Bearer ${freshToken}` };
        return fetch(url, { ...options, headers: retryHeaders });
      }
    }
    throw e;
  }

  // 401 = expired token — refresh and retry once
  if (res.status === 401 && token) {
    console.log('[apiFetch] 401 on', path, '— refreshing token');
    const freshToken = await refreshAccessToken();
    if (freshToken) {
      const retryHeaders = { ...headers, 'Authorization': `Bearer ${freshToken}` };
      return fetch(url, { ...options, headers: retryHeaders });
    }
    console.warn('[apiFetch] Refresh failed, cannot retry', path);
  }

  return res;
}
